import axios from 'axios';
import { removeSlash } from './api';
import { getConfig } from '../util/configUtil';
import config from './config';

// URL Config
const prescriptionApi = getConfig('prescriptionApi');

const cis2Axios = axios.create({
    baseURL: prescriptionApi?.cis2,
});

// NHS CIS2 API service
export const cis2Api = {
    buildLoginRedirectUrl({ redirectUrl, prompt }) {
        if (!prescriptionApi?.cis2) {
            alert('CIS2 API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        return (
            prescriptionApi?.cis2 +
            `/auth/login?redirect_to=${encodeURIComponent(redirectUrl)}` +
            (prompt ? `&prompt=${encodeURIComponent(prompt)}` : '')
        );
    },

    async refreshToken({ refreshToken, signal }) {
        if (!prescriptionApi?.cis2) {
            alert('CIS2 API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        const data = {
            refresh_token: refreshToken,
        };

        const cfg = {
            signal,
            stopRetry: true,
            params: {},
        };

        return new Promise(function (resolve, reject) {
            return cis2Axios
                .post(prescriptionApi?.cis2 + `/auth/refresh`, data, cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    async logout({ idToken, redirectUrl, signal }) {
        if (!prescriptionApi?.cis2) {
            alert('CIS2 API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        const data = {
            id_token: idToken,
            //redirect_to: redirectUrl,
        };

        const cfg = {
            signal,
            maxRedirects: 0, // prevent from redirection as we want to consume the logout response and handle redirection by our self
            params: {
                redirect_to: redirectUrl,
                response_mode: 'body',
            },
        };

        return new Promise(function (resolve, reject) {
            return cis2Axios
                .post(prescriptionApi?.cis2 + `/auth/logout`, data, cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve({ status: response.status, data: response.data });
                    } else if (response.status === 302) {
                        let redirect = response.headers['Location'];
                        resolve({ status: response.status, redirect: redirect });
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
    buildLogoutRedirectUrl({ redirectUrl }) {
        if (!prescriptionApi?.cis2) {
            alert('CIS2 API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        return `https://am.nhsint.auth-ptl.cis2.spineservices.nhs.uk/openam/oauth2/realms/root/realms/NHSIdentity/realms/Healthcare/connect/endSession?post_logout_redirect_uri=${encodeURIComponent(redirectUrl)}`;
    },
};

// NHS PDS API service for patient details and validation
export const prescriptionPatientApi = {
    async getPatientFromPDS({ nhsNumber, cis2token, signal, stopRetry = true }) {
        if (!prescriptionApi?.patient) {
            alert('Patient API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        const cfg = {
            signal,
            stopRetry,
            params: {
                cis2_token: cis2token,
            },
        };

        return new Promise(function (resolve, reject) {
            return axios
                .get(prescriptionApi?.patient + `/patients/${encodeURIComponent(nhsNumber)}`, cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
};

// NHS Terminology Server API service for patient details and validation
export const prescriptionTerminologyApi = {
    async searchMedicine({ searchTerm, maxResults = 50, signal, stopRetry = true }) {
        if (!prescriptionApi?.medicine) {
            alert('Terminology API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        const cfg = {
            signal,
            stopRetry,
        };

        return new Promise(function (resolve, reject) {
            return axios
                .get(prescriptionApi?.medicine + `/medicine/search?query=${encodeURIComponent(searchTerm)}&count=${maxResults}`, cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
    async getDetails({ snomedCode, signal, stopRetry = true }) {
        if (!prescriptionApi?.medicine) {
            alert('Terminology API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        const cfg = {
            signal,
            stopRetry,
        };

        return new Promise(function (resolve, reject) {
            return axios
                .get(prescriptionApi?.medicine + `/medicine/${encodeURIComponent(snomedCode)}`, cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
};

// NHS Directory of Healthcare API service for organization details and validation
export const directoryOfHealthcareApi = {
    async searchOrganization({ searchTerm, signal, stopRetry = true }) {
        if (!prescriptionApi?.organization) {
            alert('Directory of Healthcare API URL is not configured. Please check the configuration and try again.');
            return null;
        }

        const query = {
            query: searchTerm,
            is_eps_enabled: true,
            skip: 0,
            limit: 10,
        };

        const cfg = {
            signal,
            stopRetry,
        };

        return new Promise(function (resolve, reject) {
            return axios
                .post(prescriptionApi?.organization + '/', query, cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
};

// NHS Prescription API
export const nhsPrescriptionApi = {
    async list({ piwId, patientId, cis2token, signal }) {
        if (!prescriptionApi?.prescription) {
            alert('NHS Prescription API. Please check the configuration and try again.');
            return null;
        }

        const cfg = {
            signal,
            params: {
                piw_id: piwId,
                // patient_id: removeSlash(patientId),
                patient_id: patientId,
                cis2_token: cis2token,
            },
        };

        return new Promise(function (resolve, reject) {
            return axios
                .get(prescriptionApi?.prescription + '/prescriptions/list', cfg)
                .then(function (response) {
                    if (response.status === 200) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
    async create({ prescription, signal, stopRetry = true }) {
        if (!prescriptionApi?.prescription) {
            alert('NHS Prescription API. Please check the configuration and try again.');
            return null;
        }

        const cfg = {
            signal,
            stopRetry,
        };

        return new Promise(function (resolve, reject) {
            return axios
                .post(prescriptionApi?.prescription + '/prescriptions/create', prescription, cfg)
                .then(function (response) {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(response.data);
                    } else {
                        resolve(null);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },
};
